import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, addDoc, increment, doc, setDoc, getDocs } from 'firebase/firestore';
import app from '../../services/firebase';
import TopNavbar from '../TopNavbar/TopNavbar';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import ErrorBubble from '../ErrorBubble/ErrorBubble'; // Import the reusable ErrorBubble component

const DataInputPage = () => {
  const navigate = useNavigate();
  const auth = getAuth(app);
  const db = getFirestore(app);

  const [caloriesConsumed, setCaloriesConsumed] = useState('');
  const [caloriesBurned, setCaloriesBurned] = useState('');
  const [steps, setSteps] = useState('');
  const [optionalFields, setOptionalFields] = useState<Record<string, string>>({});
  const [newFieldKey, setNewFieldKey] = useState('Pushups'); // Default dropdown value
  const [newFieldValue, setNewFieldValue] = useState('');
  const [selectedDay, setSelectedDay] = useState<Date | undefined>(undefined);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate('/');
      }
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  const handleAddOptionalField = () => {
    if (newFieldKey.trim() && newFieldValue.trim() && !isNaN(Number(newFieldValue))) {
      setOptionalFields((prev) => ({ ...prev, [newFieldKey]: newFieldValue }));
      setNewFieldValue(''); // Clear only the value field
    } else {
      showError('Optional field value must be a number.');
    }
  };

  const handleRemoveOptionalField = (key: string) => {
    setOptionalFields((prev) => {
      const updatedFields = { ...prev };
      delete updatedFields[key];
      return updatedFields;
    });
  };

  const showError = (error: string) => {
    setMessage(error);
    setTimeout(() => setMessage(null), 3000); // Hide the error after 3 seconds
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    const user = auth.currentUser;
    if (!user) {
      showError('You must be logged in to submit data.');
      return;
    }

    // Validation: Ensure all mandatory fields are filled
    if (!selectedDay) {
      showError('Please select a date.');
      return;
    }

    if (!caloriesConsumed.trim()) {
      showError('Calories Consumed is required.');
      return;
    }

    if (!caloriesBurned.trim()) {
      showError('Calories Burned is required.');
      return;
    }

    if (!steps.trim()) {
      showError('Steps is required.');
      return;
    }

    const dateString = selectedDay.toISOString().split('T')[0];

    try {
      // Reference to the user's `data` subcollection
      const dataCollectionRef = collection(db, `users/${user.uid}/data`);

      // Query for an existing document with the same date
      const querySnapshot = await getDocs(dataCollectionRef);
      let existingDocId: string | null = null;
      let previousCaloriesBurned = 0;
      let previousSteps = 0;

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.dateString === dateString) {
          existingDocId = doc.id; // Save the document ID if a match is found
          previousCaloriesBurned = data.caloriesBurned || 0;
          previousSteps = data.steps || 0;
        }
      });

      if (existingDocId) {
        // Show a confirmation popup if data already exists for the selected date
        const proceed = window.confirm(
          `Data already exists for ${dateString}. Do you want to overwrite it?`
        );
        if (!proceed) {
          setMessage('Submission canceled.');
          return;
        }
      }

      if (existingDocId) {
        const existingDocRef = doc(db, `users/${user.uid}/data`, existingDocId);
        await setDoc(
          existingDocRef,
          {
            caloriesConsumed: Number(caloriesConsumed),
            caloriesBurned: Number(caloriesBurned),
            steps: Number(steps),
            dateString,
            userId: user.uid,
            ...optionalFields, // Include optional fields
          },
          { merge: true } // Merge to avoid overwriting other fields
        );
        setMessage('Data updated successfully!');
      } else {
        // Add a new document if no duplicate is found
        await addDoc(dataCollectionRef, {
          caloriesConsumed: Number(caloriesConsumed),
          caloriesBurned: Number(caloriesBurned),
          steps: Number(steps),
          dateString,
          userId: user.uid,
          ...optionalFields, // Include optional fields
        });
        setMessage('Data submitted successfully!');
      }

      // Update the `monthlyData` collection
      const caloriesDocRef = doc(db, 'monthlyData', 'caloriesBurned');
      const stepsDocRef = doc(db, 'monthlyData', 'stepsTaken');

      await setDoc(
        caloriesDocRef,
        {
          [user.uid]: {
            total: increment(Number(caloriesBurned) - previousCaloriesBurned), // Adjust by the difference
          },
        },
        { merge: true }
      );

      await setDoc(
        stepsDocRef,
        {
          [user.uid]: {
            total: increment(Number(steps) - previousSteps), // Adjust by the difference
          },
        },
        { merge: true }
      );

      // Clear all fields, including optional fields
      setCaloriesConsumed('');
      setCaloriesBurned('');
      setSteps('');
      setSelectedDay(undefined);
      setOptionalFields({}); // Clear optional fields
    } catch (error) {
      console.error('Error submitting data:', error);
      showError('Failed to submit data. Please try again.');
    }
  };

  const restrictToNumbers = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['e', 'E', '+', '-'].includes(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <div>
      <TopNavbar />
      <div className="page-container">
        <h1>Data Input</h1>
        {/* Use the ErrorBubble component */}
        <ErrorBubble message={message} type={message?.includes('successfully') ? 'success' : 'error'} />
        <form className="data-input-form" onSubmit={handleSubmit}>
          {/* Date Picker Section */}
          <div style={{ textAlign: 'center' }}>
            <h3>
              Select a Date <span className="required-asterisk">*</span>
            </h3>
            <div style={{ display: 'inline-block' }}>
              <DayPicker
                mode="single"
                selected={selectedDay}
                onSelect={setSelectedDay}
                disabled={{ before: new Date(2025, 4, 1), after: new Date() }} // Disable dates before May 1, 2025, and future dates
              />
            </div>
            {selectedDay && <p>Selected date: {selectedDay.toDateString()}</p>}
          </div>

          <div>
            <label htmlFor="caloriesConsumed">
              Calories Consumed <span className="required-asterisk">*</span>
            </label>
            <input
              id="caloriesConsumed"
              type="number"
              placeholder="Enter calories consumed"
              value={caloriesConsumed}
              onChange={(e) => setCaloriesConsumed(e.target.value)}
              onKeyDown={restrictToNumbers}
            />
          </div>
          <div>
            <label htmlFor="caloriesBurned">
              Calories Burned <span className="required-asterisk">*</span>
            </label>
            <input
              id="caloriesBurned"
              type="number"
              placeholder="Enter calories burned"
              value={caloriesBurned}
              onChange={(e) => setCaloriesBurned(e.target.value)}
              onKeyDown={restrictToNumbers}
            />
          </div>
          <div>
            <label htmlFor="steps">
              Steps <span className="required-asterisk">*</span>
            </label>
            <input
              id="steps"
              type="number"
              placeholder="Enter steps"
              value={steps}
              onChange={(e) => setSteps(e.target.value)}
              onKeyDown={restrictToNumbers}
            />
          </div>

          {/* Optional Fields Section */}
          <div>
            <h3>Optional Fields</h3>
            {Object.entries(optionalFields).map(([key, value]) => (
              <div key={key} className="optional-field">
                <span>
                  <strong>{key}:</strong> {value}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveOptionalField(key)}
                  className="remove-optional-field"
                >
                  Remove
                </button>
              </div>
            ))}
            <div className="add-optional-field">
              <select
                value={newFieldKey}
                onChange={(e) => setNewFieldKey(e.target.value)}
              >
                <option value="Pushups">Pushups</option>
                <option value="Pullups">Pullups</option>
                <option value="Situps">Situps</option>
                <option value="Squats">Squats</option>
              </select>
              <input
                type="number"
                placeholder="Field Value"
                value={newFieldValue}
                onChange={(e) => setNewFieldValue(e.target.value)}
                onKeyDown={restrictToNumbers}
              />
              <button type="button" onClick={handleAddOptionalField}>
                Add Field
              </button>
            </div>
          </div>

          <button type="submit">Submit</button>
        </form>
      </div>
    </div>
  );
};

export default DataInputPage;